import React, { useState } from 'react';
import { useDAWStore } from '../../store/daw-store';
import { Power, Info, RefreshCw } from 'lucide-react';

/**
 * PluginDelayCompensation Component
 * 
 * Displays and manages plugin delay compensation across all mixer tracks.
 * Automatically detects plugin latency and applies compensation to maintain sync.
 * 
 * Requirements: 13.1, 13.2, 13.3, 13.4, 13.5
 */
export const PluginDelayCompensation: React.FC = () => {
  const {
    mixerTracks,
    delayCompensationEnabled,
    maxPluginLatency,
    setDelayCompensationEnabled,
    recalculateDelayCompensation,
  } = useDAWStore();

  const [showInfo, setShowInfo] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(Date.now());

  // Track when compensation changes to show real-time updates
  React.useEffect(() => {
    setLastUpdate(Date.now());
  }, [maxPluginLatency, mixerTracks]);

  // Calculate total latency for each track
  const getTrackLatency = (trackId: string) => {
    const track = mixerTracks.find((t) => t.id === trackId);
    if (!track) return 0;
    
    return track.effects
      .filter((fx) => fx.enabled)
      .reduce((sum, fx) => sum + (fx.latency || 0), 0);
  };

  // Convert samples to milliseconds (assuming 44.1kHz sample rate)
  const samplesToMs = (samples: number) => {
    return (samples / 44100 * 1000).toFixed(2);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-white">Plugin Delay Compensation</h3>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="p-1 rounded hover:bg-white/10 transition-colors"
            title="Show information"
          >
            <Info className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Recalculate button */}
          <button
            onClick={recalculateDelayCompensation}
            className="flex items-center gap-2 px-3 py-1.5 rounded bg-white/5 hover:bg-white/10 transition-colors"
            title="Recalculate compensation"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="text-sm">Recalculate</span>
          </button>
          
          {/* Enable/Disable toggle */}
          <button
            onClick={() => setDelayCompensationEnabled(!delayCompensationEnabled)}
            className={`flex items-center gap-2 px-4 py-2 rounded transition-colors ${
              delayCompensationEnabled
                ? 'bg-indigo-600 hover:bg-indigo-700'
                : 'bg-white/10 hover:bg-white/20'
            }`}
          >
            <Power className="w-4 h-4" />
            <span className="text-sm font-medium">
              {delayCompensationEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </button>
        </div>
      </div>

      {/* Info panel */}
      {showInfo && (
        <div className="p-4 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
          <p className="text-sm text-gray-300 leading-relaxed">
            Plugin Delay Compensation automatically detects latency introduced by effects plugins
            and delays other tracks to maintain perfect sync. When enabled, tracks with less latency
            are delayed to match the track with the highest latency.
          </p>
        </div>
      )}

      {/* Status summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 rounded-lg bg-white/5">
          <div className="text-xs text-gray-400 mb-1">Status</div>
          <div className={`text-lg font-semibold ${
            delayCompensationEnabled ? 'text-green-400' : 'text-gray-400'
          }`}>
            {delayCompensationEnabled ? 'Active' : 'Inactive'}
          </div>
          {delayCompensationEnabled && (
            <div className="text-xs text-gray-500 mt-1">
              Updated {Math.floor((Date.now() - lastUpdate) / 1000)}s ago
            </div>
          )}
        </div>
        
        <div className="p-4 rounded-lg bg-white/5">
          <div className="text-xs text-gray-400 mb-1">Max Latency</div>
          <div className="text-lg font-semibold text-white">
            {samplesToMs(maxPluginLatency)} ms
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {maxPluginLatency} samples
          </div>
        </div>
        
        <div className="p-4 rounded-lg bg-white/5">
          <div className="text-xs text-gray-400 mb-1">Tracks Compensated</div>
          <div className="text-lg font-semibold text-white">
            {mixerTracks.filter((t) => t.delayCompensation > 0).length} / {mixerTracks.length}
          </div>
        </div>
      </div>

      {/* Track latency table */}
      <div className="rounded-lg border border-white/10 overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Track
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Plugin Latency
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Compensation
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Active Effects
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {mixerTracks.map((track) => {
              const trackLatency = getTrackLatency(track.id);
              
              return (
                <tr key={track.id} className="hover:bg-white/5 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-white">
                    {track.name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-300">
                    {samplesToMs(trackLatency)} ms
                    <span className="text-xs text-gray-500 ml-2">
                      ({trackLatency} samples)
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {delayCompensationEnabled ? (
                      <span className={track.delayCompensation > 0 ? 'text-indigo-400' : 'text-gray-500'}>
                        {samplesToMs(track.delayCompensation)} ms
                        <span className="text-xs text-gray-500 ml-2">
                          ({track.delayCompensation} samples)
                        </span>
                      </span>
                    ) : (
                      <span className="text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400">
                    {track.effects.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {track.effects.map((fx) => (
                          <span
                            key={fx.id}
                            className={`px-2 py-0.5 rounded text-xs ${
                              fx.enabled
                                ? 'bg-indigo-500/20 text-indigo-300'
                                : 'bg-white/5 text-gray-500 line-through'
                            }`}
                            title={`${fx.type}: ${fx.latency || 0} samples ${fx.enabled ? '(active)' : '(bypassed)'}`}
                          >
                            {fx.type}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-600">None</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Warning if compensation is disabled */}
      {!delayCompensationEnabled && maxPluginLatency > 0 && (
        <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-yellow-400 mb-1">
                Delay Compensation Disabled
              </div>
              <p className="text-sm text-gray-300">
                Plugin latency detected but compensation is disabled. Tracks may be out of sync.
                Enable compensation to maintain perfect timing across all tracks.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
